import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { Box, Button, Display, Link, Select } from '@gousto-internal/citrus-react'
// eslint-disable-next-line @library/dependency-radar/valid-imports
import { useFormikContext } from 'formik'

import { DeliveryPoint, fetchPostcodeAddresses } from '@library/api-postcode-lookup'

import { AddressCard } from 'routes/Checkout/Components/PostcodeAddressFields/AddressCard'
import {
  PostcodeInput,
  HouseNoInput,
  StreetInput,
  TownInput,
} from 'routes/Checkout/Components/PostcodeAddressFields/Fields'
import { useCheckCanDeliver } from 'routes/Checkout/Components/PostcodeAddressFields/useCheckCanDeliver'
import { showAddress } from 'routes/Checkout/utils/delivery'

export type AddressFields = {
  searchPostcode: string
  postcode: string
  houseNo: string
  street: string
  town: string
  county: string
  udprn: string
}

const emptyAddress: Partial<AddressFields> = {
  postcode: '',
  houseNo: '',
  street: '',
  town: '',
  county: '',
  udprn: '',
}

const generateSelectOptions = (points: DeliveryPoint[]) => {
  if (points.length === 0) return [{ value: 'placeholder', label: 'No addresses found' }]

  const options = points.map((point) => {
    const addressParts = [point.line1, point.line2]

    const label = addressParts.filter(Boolean).join(', ')

    return { value: point.udprn, label }
  })

  return [{ value: 'placeholder', label: 'Please select your address' }, ...options]
}

export const PostcodeAddressFields = () => {
  const { values, errors, setValues, setFieldValue, setFieldError } =
    useFormikContext<AddressFields>()

  const selectedAddress = values.udprn ? showAddress(values) : undefined

  const [sentPostcode, setSentPostcode] = useState('')
  const [deliveryPoints, setDeliveryPoints] = useState<DeliveryPoint[]>([])
  const [isEditingManually, setIsEditingManually] = useState(
    !selectedAddress && (!!values.houseNo || !!values.town),
  )

  const searchAddresses = useCallback(async () => {
    try {
      const response = await fetchPostcodeAddresses(values.searchPostcode)
      setDeliveryPoints(response)
    } catch {
      setFieldError('searchPostcode', 'Please enter a valid UK postcode')
      setDeliveryPoints([])
    }
  }, [setFieldError, values.searchPostcode])

  const { checkCanDeliver } = useCheckCanDeliver(
    (error) => setFieldError('searchPostcode', error),
    setDeliveryPoints,
    values.searchPostcode,
  )

  useEffect(() => {
    const onMount = async () => {
      await searchAddresses()
      await checkCanDeliver()
      setSentPostcode(values.searchPostcode)
    }
    onMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSelectFieldChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const point = deliveryPoints.find((p) => p.udprn === e.target.value)
      if (!point) return
      setValues({
        ...values,
        houseNo: point.line1,
        street: point.line2,
        town: point.town,
        county: point.county,
        postcode: point.postcode,
        udprn: point.udprn,
      })
    },
    [deliveryPoints, setValues, values],
  )

  const onSearchButtonClick = useCallback(async () => {
    setSentPostcode(values.searchPostcode)
    await searchAddresses()
    await checkCanDeliver()
    setValues({ ...values, ...emptyAddress })
    setIsEditingManually(false)
  }, [checkCanDeliver, searchAddresses, setValues, values])

  return (
    <>
      <Box display={Display.Flex}>
        <Box
          flexGrow={1}
          flexShrink={1}
          flexBasis="100%"
          maxWidth="16.25rem"
          paddingRight={[3, 4]}
          paddingBottom={[3, 4]}
        >
          <PostcodeInput />
        </Box>

        <Button
          type="button"
          style={{ marginTop: '2rem' }}
          height="3rem"
          width="auto"
          data-testid="checkoutFindAddressButton"
          disabled={!!errors.searchPostcode || values.searchPostcode === sentPostcode}
          onClick={onSearchButtonClick}
        >
          Search
        </Button>
      </Box>

      {!!selectedAddress && (
        <AddressCard
          address={selectedAddress}
          onEditAddressManuallyClick={() => {
            setIsEditingManually(true)
            setFieldValue('udprn', '')
          }}
        />
      )}

      {!selectedAddress && isEditingManually && (
        <>
          <Box paddingBottom={[4, 6]}>
            <HouseNoInput />
          </Box>
          <Box paddingBottom={[4, 6]}>
            <StreetInput />
          </Box>
          <Box paddingBottom={[4, 6]}>
            <TownInput />
          </Box>
        </>
      )}

      {!selectedAddress && !isEditingManually && (
        <>
          <Select
            data-testid="checkoutAddressDropdown"
            style={{ maxWidth: '100%', marginBottom: '1rem' }}
            onChange={onSelectFieldChange}
          >
            {generateSelectOptions(deliveryPoints ?? []).map((option) => (
              <option
                key={option.value}
                value={option.value}
                selected={option.value === 'placeholder'}
              >
                {option.label}
              </option>
            ))}
          </Select>

          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            style={{ marginBottom: '1.5rem' }}
            size={1}
            role="button"
            onClick={() => {
              setIsEditingManually(true)
              setFieldValue('postcode', values.searchPostcode)
            }}
          >
            Enter address manually
          </Link>
        </>
      )}
    </>
  )
}
